// ============================================================
// Keyboard activation for clickable elements that are not <button>s
// ============================================================
// A clickable <div> or <span> is invisible to a keyboard user unless it
// takes focus and answers Enter and Space the way a native button does.
// Converting it to a <button> is usually better, but a button brings the
// browser's own styling, so a styled element keeps its tag and borrows
// the behaviour from here instead (jsx-a11y's click-events-have-key-events
// and no-static-element-interactions).

import type { KeyboardEvent } from 'react';

/**
 * A keydown handler that runs `action` on Enter or Space, as a native
 * button would. It prevents the default only for those two keys (Space
 * would otherwise scroll the page) and leaves every other key alone, so
 * Tab still moves focus.
 */
export function onActivateKey(action: () => void) {
  return (event: KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    action();
  };
}

/**
 * The props that make a plain element behave as a button: spread them onto
 * it (`<div {...activatable(open)}>`). `action` runs on click, Enter and
 * Space.
 */
export function activatable(action: () => void) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    onClick: () => action(),
    onKeyDown: onActivateKey(action),
  };
}
